import { useEffect, useRef, useState } from "react";
import { Minus, Plus, ImageUp, X } from "lucide-react";
import { getCardImage, toThumbUrl } from "@dm-online/shared";
import type { Card } from "@dm-online/shared";
import { api } from "@/lib/api";
import { matchCardName, resizeImage, type DeckScanResult } from "@/lib/deckScan";
import { Button } from "@/components/ui/button";
import { Typography } from "@/components/ui/typography";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DialogFooter } from "@/components/ui/dialog";

// 読み取った1行（画像上の名前 → 照合したカード・枚数）
interface ScanRow {
  scannedName: string;
  card: Card | null;
  confidence: "exact" | "fuzzy" | "none";
  candidates: Card[];
  count: number;
}

// Select は空文字値を許可しないため「取り込まない」を表すセンチネル
const SKIP = "__skip__";

interface Props {
  maxCopies: number;
  onImport: (cardIds: string[]) => void;
  onCancel: () => void;
}

// デッキリストのスクショ・実物カードの写真からデッキを読み取る（インポートダイアログの「画像」タブ）。
// 読み取りは worker 経由の Claude、カード名の照合はフロントで行い、確認リストで修正してから取り込む。
export default function DeckImageImport({ maxCopies, onImport, onCancel }: Props) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<ScanRow[] | null>(null);
  const [notes, setNotes] = useState("");

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  // クリップボードの画像（スクショの貼り付け）も受け付ける
  useEffect(() => {
    function onPaste(e: ClipboardEvent) {
      const file = Array.from(e.clipboardData?.files ?? []).find((f) => f.type.startsWith("image/"));
      if (file) scan(file);
    }
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  });

  async function scan(file: File) {
    if (scanning) return;
    setPreview(URL.createObjectURL(file));
    setRows(null);
    setNotes("");
    setError(null);
    setScanning(true);
    try {
      const { data, mediaType } = await resizeImage(file);
      const res = await api.post<DeckScanResult>("/decks/scan-image", { image: data, mediaType });
      if (!res.ok || !res.data) {
        setError(res.error ?? "画像を読み取れませんでした");
        return;
      }
      setNotes(res.data.notes);
      setRows(
        res.data.cards.map((c) => {
          const m = matchCardName(c.name);
          return { scannedName: c.name, ...m, count: Math.min(maxCopies, Math.max(1, c.count)) };
        }),
      );
      if (res.data.cards.length === 0) setError("カードを読み取れませんでした");
    } catch {
      setError("画像を読み込めませんでした");
    } finally {
      setScanning(false);
    }
  }

  function updateRow(i: number, patch: Partial<ScanRow>) {
    setRows((prev) => prev && prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  }

  function selectCard(i: number, value: string) {
    const row = rows?.[i];
    if (!row) return;
    const card = value === SKIP ? null : row.candidates.find((c) => c.id === value) ?? null;
    updateRow(i, { card, confidence: card ? "exact" : "none" });
  }

  // 同じカードに照合された行はまとめ、1種あたり maxCopies 枚までにする
  function buildCardIds(): string[] {
    const counts = new Map<string, number>();
    for (const r of rows ?? []) {
      if (!r.card) continue;
      counts.set(r.card.id, Math.min(maxCopies, (counts.get(r.card.id) ?? 0) + r.count));
    }
    return Array.from(counts).flatMap(([id, n]) => Array<string>(n).fill(id));
  }

  const importCount = buildCardIds().length;
  const unmatched = rows?.filter((r) => !r.card).length ?? 0;
  const fuzzy = rows?.filter((r) => r.confidence === "fuzzy").length ?? 0;

  return (
    <>
      <div className="space-y-3">
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file) scan(file);
          }}
        />
        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          disabled={scanning}
          className="flex w-full items-center gap-3 rounded-lg border border-dashed p-3 text-left hover:bg-accent disabled:opacity-60"
        >
          {preview ? (
            <img src={preview} alt="" className="h-16 w-16 shrink-0 rounded object-cover" />
          ) : (
            <ImageUp className="size-8 shrink-0 text-muted-foreground" />
          )}
          <span className="space-y-0.5">
            <Typography as="span" variant="body" className="block">
              {scanning ? "読み取り中...（10〜30秒ほどかかります）" : preview ? "別の画像を選ぶ" : "画像を選ぶ"}
            </Typography>
            <Typography as="span" variant="caption" tone="muted" className="block">
              デッキリストのスクリーンショット、または実物カードを並べた写真。貼り付け（Ctrl/⌘+V）でも読み取れます
            </Typography>
          </span>
        </button>

        {error && <Typography variant="caption" tone="destructive">{error}</Typography>}
        {notes && <Typography variant="caption" tone="muted">メモ: {notes}</Typography>}

        {rows && rows.length > 0 && (
          <div className="space-y-2">
            <Typography variant="caption" tone="muted">
              読み取った内容を確認してください。
              {fuzzy > 0 && ` 推定で照合したカードが${fuzzy}件あります（黄色）。`}
              {unmatched > 0 && ` 照合できなかった${unmatched}件は取り込まれません。`}
            </Typography>
            <ul className="max-h-[45vh] space-y-1.5 overflow-y-auto pr-1">
              {rows.map((row, i) => (
                <li
                  key={i}
                  className={`flex items-center gap-2 rounded-md border p-1.5 ${
                    !row.card ? "opacity-60" : row.confidence === "fuzzy" ? "border-amber-500/70 bg-amber-500/10" : ""
                  }`}
                >
                  {row.card ? (
                    <img src={toThumbUrl(getCardImage(row.card.id))} alt="" className="h-12 w-[35px] shrink-0 rounded-sm object-cover" />
                  ) : (
                    <div className="grid h-12 w-[35px] shrink-0 place-items-center rounded-sm bg-muted">
                      <X className="size-4 text-muted-foreground" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1 space-y-1">
                    <Typography variant="caption" tone="muted" className="truncate" title={row.scannedName}>
                      読み取り: {row.scannedName}
                    </Typography>
                    <Select value={row.card?.id ?? SKIP} onValueChange={(v) => selectCard(i, v)}>
                      <SelectTrigger size="sm" className="w-full text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {row.candidates.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                        <SelectItem value={SKIP}>取り込まない</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Button
                      variant="outline"
                      size="icon"
                      className="size-7"
                      aria-label="1枚減らす"
                      disabled={row.count <= 1}
                      onClick={() => updateRow(i, { count: row.count - 1 })}
                    >
                      <Minus />
                    </Button>
                    <span className="w-4 text-center text-sm tabular-nums">{row.count}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      className="size-7"
                      aria-label="1枚増やす"
                      disabled={row.count >= maxCopies}
                      onClick={() => updateRow(i, { count: row.count + 1 })}
                    >
                      <Plus />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <DialogFooter>
        <Button variant="secondary" onClick={onCancel}>
          キャンセル
        </Button>
        <Button onClick={() => onImport(buildCardIds())} disabled={scanning || importCount === 0}>
          {importCount > 0 ? `${importCount}枚を取り込む` : "取り込む"}
        </Button>
      </DialogFooter>
    </>
  );
}
